import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import PieChartOutlineRoundedIcon from '@mui/icons-material/PieChartOutlineRounded';
import MobileDrawerMenu from '#component/general/mobile-drawer-menu/mobile-drawer-menu';
import { changeItemsPerPage, changeOrderAndOrderBy } from '#action/device/devices-paginated';
import DeviceProjectsTreePopup from '#component/device/dashboard/projects-tree';
import PreloaderComponent from '#component/preloader';
import NotFoundMessage from '#component/general/not-found-message';
import { downloadFile } from '#action/download';
import UnfoldableCard from '#component/device/general/unfoldable-card/unfoldable-card';
import HighlightWord from '#component/general/highlight-word/highlight-word';
import { getHighlightedSearchResult } from '#component/general/highlight-word/helper';
import { CardsAlignment } from './styles';

const DEFAULT_ITEMS_PER_PAGE = 20;
const DEFAULT_ORDER = 'asc';
const DEFAULT_ORDER_BY = 'title';

/**
 * Renders cards with device info.
 * @returns {React.FC}
 */

const CardsView = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const {
    devices,
    status,
    order,
    orderBy,
    itemsPerPage,
    filter
  } = useSelector(s => s.devicesPaginated, shallowEqual);
  const account = useSelector(s => s.account, shallowEqual);
  const meta = useSelector(s => s.meta, shallowEqual);
  const listFilter = useSelector(s => s.listFilter, shallowEqual);
  const loading = status === 'loading';
  const highlightedSearchResult = getHighlightedSearchResult(listFilter);

  const [isDrawerMenuOpen, setIsDrawerMenuOpen] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState({});
  const [selectedDeviceId, setSelectedDeviceId] = useState(0);

  useEffect(() => {
    if (itemsPerPage !== DEFAULT_ITEMS_PER_PAGE) {
      dispatch(changeItemsPerPage(DEFAULT_ITEMS_PER_PAGE));
    }
    if (order !== DEFAULT_ORDER || orderBy !== DEFAULT_ORDER_BY) {
      dispatch(changeOrderAndOrderBy(DEFAULT_ORDER, DEFAULT_ORDER_BY));
    }
  }, [dispatch, itemsPerPage, order, orderBy]);

  const isFilterSelected = !loading && filter?.length;
  const isFilteredDataExist = isFilterSelected ?
    (filter && devices.length > 0) : devices.length > 0;

  if (!devices.length && loading) {
    return <PreloaderComponent />;
  }

  if (!loading && !isFilteredDataExist) {
    return (
      <NotFoundMessage>
        No devices
      </NotFoundMessage>
    );
  }

  const handleShowDetailsPopup = device => {
    setSelectedDevice(device);
    setShowDetailsDialog(true);
  };

  const handleDownloadQRClick = async (e, deviceId) => {
    if (deviceId) {
      const qrDevice = devices.find(device => device.id === selectedDeviceId);
      e.stopPropagation();
      const path = `report/device/${qrDevice.id}/label`;
      const fileName = `Label ${qrDevice.title || qrDevice.uid}.pdf`;

      await dispatch(downloadFile(
        path,
        fileName
      ));
    }
  };

  const findSelectedDevice = clickedDeviceId =>
    devices.find(device => device.id === clickedDeviceId);

  const menuItems = [
    { name: 'Metrics and events', onClick: () => history.push(`/device/${selectedDeviceId}`), icon: <PieChartOutlineRoundedIcon /> },
    { name: 'Details', onClick: e => handleShowDetailsPopup(e, selectedDevice), icon: <AccountTreeOutlinedIcon /> },
    { name: 'Download QR', onClick: e => handleDownloadQRClick(e, selectedDeviceId), icon: <WidgetsOutlinedIcon /> }
  ];

  const currentDevice = findSelectedDevice(selectedDeviceId);

  return (
    <>
      <CardsAlignment>
        {devices.map(device =>
          <UnfoldableCard
            key={device.id}
            device={device}
            account={account}
            loading={loading}
            onOpenDrawer={deviceId => {
              setIsDrawerMenuOpen(true);
              setSelectedDeviceId(deviceId);
            }}
            label={
              highlightedSearchResult ? <HighlightWord
                textToHighlight={device.title || device.uid}
                searchWord={highlightedSearchResult}
              /> : device.title || device.uid
            }
            meta={meta}
          />)}
      </CardsAlignment>
      <MobileDrawerMenu
        isOpen={isDrawerMenuOpen}
        onClose={setIsDrawerMenuOpen}
        menuTitle={devices.find(device =>
          device.id === selectedDeviceId)?.title}
        menuItems={menuItems}
      />
      {showDetailsDialog && <DeviceProjectsTreePopup
        open={showDetailsDialog}
        setOpen={setShowDetailsDialog}
        deviceUid={currentDevice.uid}
        deviceTitle={currentDevice.title}
        device={currentDevice}
        setSelectedDevice={setSelectedDevice}
      />}
    </>
  );
};

export default CardsView;
